import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { SafeErrorService } from '../../../core/services/safe-error.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  JivuCommand,
  JivuCommandDevice,
  JivuCommandHandoffPayload,
  JivuCommandType
} from './jivu-command.models';
import { JivuCommandPollerService } from './jivu-command-poller.service';
import { JivuCommandService } from './jivu-command.service';

export interface JivuCommandHandoffDraft {
  commandType: JivuCommandType;
  title: string;
  payload: JivuCommandHandoffPayload;
}

@Injectable({ providedIn: 'root' })
export class JivuCommandHandoffService {
  private readonly commandService = inject(JivuCommandService);
  private readonly poller = inject(JivuCommandPollerService);
  private readonly toast = inject(ToastService);
  private readonly safeError = inject(SafeErrorService);
  private readonly router = inject(Router);

  readonly draft = signal<JivuCommandHandoffDraft | null>(null);
  readonly selectedDeviceId = signal('');
  readonly isSubmitting = signal(false);
  readonly error = signal<string | null>(null);

  readonly devices = this.commandService.devices;
  readonly targetDevices = computed(() => {
    const draft = this.draft();
    const currentDeviceId = this.commandService.getDeviceInstanceId();
    return this.devices()
      .filter(device => !draft || device.capabilities.includes(draft.commandType))
      .sort((left, right) => {
        const leftCurrent = left.deviceInstanceId === currentDeviceId ? 0 : 1;
        const rightCurrent = right.deviceInstanceId === currentDeviceId ? 0 : 1;
        if (leftCurrent !== rightCurrent) return leftCurrent - rightCurrent;
        if (left.isOnline !== right.isOnline) return left.isOnline ? -1 : 1;
        return left.displayName.localeCompare(right.displayName);
      });
  });
  readonly activeCommand = this.commandService.activeCommand;
  readonly selectedDeviceCommands = computed(() =>
    this.commandService.activeCommandsForDevice(this.selectedDeviceId()));
  readonly setupRequired = this.commandService.setupRequired;
  readonly setupMessage = this.commandService.setupMessage;

  async open(draft: JivuCommandHandoffDraft): Promise<void> {
    this.draft.set(draft);
    this.error.set(null);
    await this.refreshDevices();
    const firstTarget = this.targetDevices()[0];
    this.selectedDeviceId.set(firstTarget?.deviceInstanceId || '');
  }

  close(): void {
    if (this.isSubmitting()) return;
    this.draft.set(null);
    this.error.set(null);
  }

  async refreshDevices(): Promise<void> {
    try {
      await firstValueFrom(this.commandService.heartbeatAndPoll());
      await firstValueFrom(this.commandService.overview());
    } catch (error) {
      this.error.set(this.safeError.message(error, { fallback: 'Unable to load Jivu Command devices.' }));
    }
  }

  async send(): Promise<void> {
    const draft = this.draft();
    const targetDeviceInstanceId = this.selectedDeviceId();
    if (!draft || !targetDeviceInstanceId) {
      this.error.set('Select a target device.');
      return;
    }

    const target = this.targetDevices().find(device => device.deviceInstanceId === targetDeviceInstanceId);
    if (!target) {
      this.error.set('Select a registered device that supports this command.');
      return;
    }

    this.isSubmitting.set(true);
    this.error.set(null);
    try {
      const result = await firstValueFrom(this.commandService.createCommand({
        targetDeviceInstanceId,
        commandType: draft.commandType,
        title: draft.title,
        payload: draft.payload
      }));

      if (!result.success) {
        this.error.set(result.message || 'Unable to queue command.');
        return;
      }

      this.toast.success('Jivu Command sent to target device.');
      this.clearDraft();
      await this.afterAcceptedCommand(targetDeviceInstanceId);
    } catch (error: any) {
      this.error.set(this.safeError.message(error, { fallback: 'Unable to send Jivu Command.' }));
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /**
   * Send a command DIRECTLY to a specific device WITHOUT opening the picker dialog — used to route
   * "Generate" through Command-to-self in one action. Mirrors send() (same createCommand + self
   * fast-path: createCommand sets the accept flag for a self-target, then pollOnce claims it within
   * ~1-3s). Returns true on success. Additive — does not change the dialog send() path.
   */
  async sendDirect(draft: JivuCommandHandoffDraft, targetDeviceInstanceId: string): Promise<boolean> {
    if (!draft || !targetDeviceInstanceId) return false;
    try {
      const result = await firstValueFrom(this.commandService.createCommand({
        targetDeviceInstanceId,
        commandType: draft.commandType,
        title: draft.title,
        payload: draft.payload
      }));
      if (!result.success) {
        const message = result.message
          || 'This device is busy. Complete or cancel a command in the Command Center before sending more.';
        this.error.set(message);
        // sendDirect/sendToSelf runs WITHOUT a picker dialog, so surface the rejection (e.g. the
        // "queue is full" device limit) as a toast. The Jivu Command queue is the sole concurrency
        // gate now — the caller must NOT fall back to a direct render, so the limit is respected.
        this.toast.show(message, 'warning', 6000);
        return false;
      }
      await this.afterAcceptedCommand(targetDeviceInstanceId);
      return true;
    } catch (error: any) {
      const message = this.safeError.message(error, { fallback: 'Unable to send Jivu Command.' });
      this.error.set(message);
      this.toast.error(message);
      return false;
    }
  }

  /** Convenience: send a command to THIS device (the Command-to-self path that replaces Generate). */
  sendToSelf(draft: JivuCommandHandoffDraft): Promise<boolean> {
    return this.sendDirect(draft, this.commandService.getDeviceInstanceId());
  }

  deviceLabel(device: JivuCommandDevice): string {
    const current = this.sameDevice(device.deviceInstanceId, this.commandService.getDeviceInstanceId()) ? 'this device, ' : '';
    const status = device.isOnline ? 'online' : 'offline - will run when opened';
    const queue = this.queueLabel(this.commandService.activeCommandsForDevice(device.deviceInstanceId));
    return `${device.displayName} (${current}${device.deviceType}, ${status}${queue})`;
  }

  selectedDeviceStatus(): string {
    const commands = this.selectedDeviceCommands();
    if (!commands.length) return 'Available for a new command.';
    const running = commands.find(command => command.status === 'claimed' || command.status === 'running');
    const queuedCount = commands.filter(command => command.status === 'queued').length;
    if (running && queuedCount) return `Running ${this.commandLabel(running)} with ${queuedCount} queued behind it.`;
    if (running) return `Running ${this.commandLabel(running)}. New work will queue behind it.`;
    return `${queuedCount} command${queuedCount === 1 ? '' : 's'} queued for this device.`;
  }

  private queueLabel(commands: JivuCommand[]): string {
    if (!commands.length) return '';
    const running = commands.some(command => command.status === 'claimed' || command.status === 'running');
    const queuedCount = commands.filter(command => command.status === 'queued').length;
    const parts = [
      running ? 'busy' : '',
      queuedCount ? `${queuedCount} queued` : ''
    ].filter(Boolean);
    return parts.length ? `, ${parts.join(', ')}` : '';
  }

  private commandLabel(command: JivuCommand): string {
    return command.title || command.commandType.replace(/_/g, ' ');
  }

  private clearDraft(): void {
    this.draft.set(null);
    this.error.set(null);
  }

  private async afterAcceptedCommand(targetDeviceInstanceId: string): Promise<void> {
    if (this.sameDevice(targetDeviceInstanceId, this.commandService.getDeviceInstanceId())) {
      try {
        await this.poller.pollOnce();
      } catch {
        // The command is already queued; the Command Center will keep polling after navigation.
      }
    }

    await this.router.navigate(['/jivu-command']);
  }

  private sameDevice(left?: string | null, right?: string | null): boolean {
    return String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase();
  }
}
